import { Component, OnInit, Inject, Optional } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { DropdownHttpService } from '@core/services/dropdown-http.service';
import { LocaleHttpService } from '@core/services/locale-http.service';
import { Observable, Subscription } from 'rxjs';
import { finalize, map } from 'rxjs/operators';
import { EventEmitterService } from '@core/services/event-emitter.service';

@Component({
  selector: 'kt-promotion',
  templateUrl: './promotion-name-setting.component.html',
  styleUrls: ['./promotion-name-setting.component.scss']
})
export class PromotionNameSettingComponent implements OnInit {
  form: FormGroup;
  activeCurrencyId: number = 0;
  filteredLocales: Observable<any[]>;
  allCurrencyData: { [currencyId: number]: any } = {};
  promotionNameControls: { [localeId: number]: FormControl } = {};
  rewardNameControls: { [localeId: number]: FormControl } = {};
  dropdown = {
    currencies: [] as any[]
  };
  currenciesLoading = false;  // the currency tabs come from an HTTP call — show it instead of an empty row
  localesLoading = false;     // locale rows are fetched per currency — same reason
  promotionNamePreviews: { [localeId: number]: string } = {};
  rewardNamePreviews: { [localeId: number]: string } = {};
  
  
  private formSubscription: Subscription;
  private saveSubscription: Subscription;

  constructor(
    private LocaleHttpService: LocaleHttpService,
    private dropdownHttpService: DropdownHttpService,
    @Optional() @Inject('initialData') private initialData: any,
    private eventEmitterService: EventEmitterService,
  ) { }

  ngOnInit() {
    // Free Spin supports all currencies; PP / BTG are limited to MYR.
    const allCurrencies = JSON.parse(sessionStorage.getItem('currencies')) || [];
    this.dropdown.currencies = this.initialData?.promotion_type == 'Free Spin'
      ? allCurrencies
      : allCurrencies.filter((c: any) => ['MYR'].includes(c.name));

    this.formInit();
    this.eventEmitterService.onPromotionNameSettingData(this.getFormData());
    this.formSubscription = this.form.valueChanges.subscribe(() => {
      this.saveCurrentCurrencyData();
      this.eventEmitterService.onPromotionNameSettingData(this.getFormData());
    });

    this.loadCurrencies();
  }

  ngOnDestroy() {
  this.saveCurrentCurrencyData(); 
    if (this.formSubscription) {
      this.formSubscription.unsubscribe();
    }
    if (this.saveSubscription) {
      this.saveSubscription.unsubscribe();
    }
  }

  private loadCurrencies() {
    if (this.dropdown.currencies.length === 0) {
      this.currenciesLoading = true;
      this.dropdownHttpService.currencies.subscribe({
        next: res => {
          this.dropdown.currencies = res;
          this.currenciesLoading = false;
          if (this.dropdown.currencies.length > 0) {
            this.selectCurrency(this.dropdown.currencies[0].id);
          }
        },
        error: () => this.currenciesLoading = false,
      });
    } else if (this.dropdown.currencies.length > 0) {
      this.selectCurrency(this.dropdown.currencies[0].id);
    }
  }

  selectCurrency(currencyId: number): void {
    this.saveCurrentCurrencyData();
    this.activeCurrencyId = currencyId;

    this.promotionNameControls = {};
    this.rewardNameControls = {};
    Object.keys(this.form.controls).forEach(key => this.form.removeControl(key));

    const existingData = this.allCurrencyData[currencyId] 
      ?? this.initialData?.promotion_names?.find((x: any) => x.currency_id === currencyId);

    this.localesLoading = true;
    this.filteredLocales = this.LocaleHttpService.getLocales(currencyId).pipe(
      // Guarded by currency: switching tabs mid-request unsubscribes the previous stream, and its
      // finalize would otherwise clear the flag for the request that is still in flight.
      finalize(() => { if (this.activeCurrencyId === currencyId) this.localesLoading = false; }),
      map((locales: any[]) => {
        locales.forEach(locale => {
          const promotionNameKey = `promotion_name_${locale.id}`;
          const rewardNameKey = `reward_name_${locale.id}`;

          const existingLocale = existingData?.locales?.find((l: any) => {
            const lid = l.locale_id ?? l.id ?? l.settings_locale_id ?? l.localeId;
            return lid === locale.id;
          });

          const promotionValue = existingLocale?.promotion_name || '';
          const rewardValue = existingLocale?.reward_name || '';

          this.promotionNameControls[locale.id] = new FormControl(existingLocale?.promotion_name || '');
          this.rewardNameControls[locale.id] = new FormControl(existingLocale?.reward_name || '');

          this.form.addControl(promotionNameKey, this.promotionNameControls[locale.id]);
          this.form.addControl(rewardNameKey, this.rewardNameControls[locale.id]);

          this.promotionNamePreviews[locale.id] = promotionValue || '*Name';
          this.rewardNamePreviews[locale.id] = rewardValue || '*Name';
        });
        return locales;
      })
    );
  }

  private saveCurrentCurrencyData(): void {
    if (!this.activeCurrencyId) return;

    const locales: any[] = [];
    Object.keys(this.promotionNameControls).forEach(localeId => {
      const localeIdNum = Number(localeId);
      const promotion = this.promotionNameControls[localeIdNum].value;
      const reward = this.rewardNameControls[localeIdNum].value;
      if (promotion || reward) {
        locales.push({
          locale_id: localeIdNum,
          promotion_name: promotion,
          reward_name: reward
        });
      }
    });

    if (locales.length > 0) {
      this.allCurrencyData[this.activeCurrencyId] = {
        currency_id: this.activeCurrencyId,
        locales
      };
    }
  }


  getPromotionNameControl(localeId: number): FormControl {
    return this.promotionNameControls[localeId] || new FormControl(null);
  }

  getRewardNameControl(localeId: number): FormControl {
    return this.rewardNameControls[localeId] || new FormControl(null);
  }

  getFormData(): any {
    this.saveCurrentCurrencyData();
    return Object.values(this.allCurrencyData);
  }

  hasData(): boolean {
    return Object.keys(this.allCurrencyData).length > 0;
  }

  private formInit() {
    this.form = new FormGroup({});
    const existingData = this.initialData?.promotion_names;
    
    if (Array.isArray(existingData)) {
      existingData.forEach((entry: any) => {
        const cid = entry.currency_id;
        const locales = Array.isArray(entry.locales) ? entry.locales : [];
        this.allCurrencyData[cid] = {
          currency_id: cid,
          locales: locales.map((x: any) => ({
            locale_id: x.locale_id ?? x.id ?? x.settings_locale_id ?? x.localeId,
            promotion_name: x.promotion_name ?? x.promotionName ?? null,
            reward_name: x.reward_name ?? x.rewards_name ?? null
          }))
        };
      });
    }
  }

  onPromotionNameInput(event: Event, localeId: number) {
    const input = event.target as HTMLInputElement;
    this.promotionNamePreviews[localeId] = input.value || '*Name';
  }

  onRewardNameInput(event: Event, localeId: number) {
    const input = event.target as HTMLInputElement;
    this.rewardNamePreviews[localeId] = input.value || '*Name';
  }

}
